import { VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import { useGetPhonePrefixes } from "hooks/phone";
import { useUpdateUser, useCreateAccountAsAdmin } from "hooks/users";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  FullscreenModal,
  Input,
  PhoneWithPrefix,
  SelectComponent,
  Warning,
  Switch,
  DropFileInput,
} from "shared";
import { UserType } from "types/UsersTypes";
import * as Yup from "yup";
import "./edit-user.scss";
import { validatePhoneNumber } from "utils/validation/phoneValidation";

interface EditUserByAdminProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: UserType | null;
}

const EditUserByAdmin: React.FC<EditUserByAdminProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}) => {
  const { mutateAsync: updateUser } = useUpdateUser();
  const { mutateAsync: createUser } = useCreateAccountAsAdmin();
  const { data: phonePrefixes } = useGetPhonePrefixes();
  const formRef = useRef<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [showRodoCommunicationWarning, setShowRodoCommunicationWarning] =
    useState(false);
  const isUserSignedRodo = !!user?.rodoAcceptedAt;
  const initialValues = {
    name: user?.name || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    phonePrefix: user?.phonePrefixUuid || "",
    rodoPreference: user?.rodoPreference || "",
    file: undefined,
  };

  const handleOnClose = () => {
    onClose();
    setShowPhoneWarning(false);
    setShowEmailWarning(false);
    setShowRodoCommunicationWarning(false);
  };

  useEffect(() => {
    isOpen && setShowRodoCommunicationWarning(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && phonePrefixes && formRef.current) {
      if (!!user) {
        const phonePrefix = phonePrefixes.find(
          (prefix) => prefix.value === user?.phonePrefixUuid
        );
        formRef.current.setFieldValue(
          "phonePrefix",
          phonePrefix?.value || phonePrefixes[0]?.value
        );
      } else {
        const pl = phonePrefixes.find((prefix) => prefix.label === "+48");
        formRef.current.setFieldValue("phonePrefix", pl?.value);
      }
    }
  }, [phonePrefixes, user, isOpen, isUserSignedRodo]);

  const onSubmit = async (values: typeof initialValues) => {
    setIsSubmitting(true);
    setShowPhoneWarning(false);
    setShowEmailWarning(false);

    try {
      if (user) {
        await updateUser(
          //@ts-ignore
          {
            ...user,
            ...values,
            phonePrefixUuid: values.phonePrefix,
          },
          {
            onSuccess: () => {
              onSuccess && onSuccess();
              handleOnClose();
            },
            onError: (error) => {
              handleErrors(error);
            },
          }
        );
      } else {
        // Create new user
        await createUser(
          {
            name: values.name,
            phoneNumber: values.phoneNumber,
            phonePrefixUuid: values.phonePrefix,
            email: values.email,
            rodoPreference: values.rodoPreference,
            file: values.file,
          },
          {
            onSuccess: () => {
              onSuccess && onSuccess();
              handleOnClose();
            },
            onError: (error) => {
              handleErrors(error);
            },
          }
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleErrors = (error: any) => {
    console.log(error?.data?.message);
    switch (error?.data?.message) {
      case "EMAIL_EXIST":
        setShowEmailWarning(true);
        break;
      case "PHONE_NUMBER_EXIST":
        setShowPhoneWarning(true);
        break;
      default:
        break;
    }
  };

  const validation = () =>
    Yup.object().shape({
      name: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .max(50)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        ),
      email: Yup.string()
        .nullable()
        .when("rodoPreference", {
          is: "email",
          then: (schema) =>
            schema
              .email(VALIDATION_ENUM.WRONG_EMAIL)
              .required(VALIDATION_ENUM.REQUIRED)
              .matches(
                /^[a-zA-Z0-9_%+-]+([.][a-zA-Z0-9_%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                VALIDATION_ENUM.WRONG_EMAIL
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      phoneNumber: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .test("is-digitals-only", VALIDATION_ENUM.NUMBER, (value) => {
          return /^\d+$/.test(value);
        })
        .test("phone-validation", VALIDATION_ENUM.NUMBER, function (value) {
          if (!/^\d+$/.test(value)) {
            return false;
          }

          const { phonePrefix } = this.parent;

          if (value && phonePrefix && phonePrefixes?.length) {
            const validation = validatePhoneNumber(
              value,
              phonePrefix,
              phonePrefixes
            );

            if (!validation.isValid) {
              return this.createError({
                message: validation.error || VALIDATION_ENUM.NUMBER,
              });
            }
          }

          return true;
        }),
      phonePrefix: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      rodoPreference: Yup.string().test(
        "validate-if-not-signed",
        "Nieprawidłowa forma komunikacji RODO",
        (value) => {
          if (isUserSignedRodo) {
            return true;
          }
          return ["email", "sms", "file"].includes(value || "");
        }
      ),
    });

  const handleChangeRodoPreference = (
    value: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (value !== "file") {
      setFieldValue("file", null);
    }
    setFieldValue("rodoPreference", value);
    setShowRodoCommunicationWarning(false);
  };

  const handleValidateRodoPreference = (rodoPreference: string) => {
    if (!rodoPreference && !user) {
      setShowRodoCommunicationWarning(true);
    }
  };

  return (
    <FullscreenModal
      isOpen={isOpen}
      onClose={handleOnClose}
      className="edit-user"
    >
      <h2 className="fullscreen-modal__header">
        {user ? "Edycja klienta" : "Dodawanie klienta"}
      </h2>
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
        innerRef={formRef}
      >
        {({ values, setFieldValue, errors }) => {
          const { file, rodoPreference } = values;
          if (file && rodoPreference !== "file") {
            setFieldValue("rodoPreference", "file");
          }
          return (
            <Form className="m-width-100">
              <Field
                type="text"
                id="name"
                name="name"
                label={"Imię i nazwisko"}
                as={Input}
              />

              <PhoneWithPrefix>
                <Field
                  label="Telefon"
                  name="phonePrefix"
                  id="phonePrefix"
                  as={SelectComponent}
                  options={phonePrefixes}
                />
                <Field
                  name="phoneNumber"
                  type="text"
                  id="phoneNumber"
                  maxLength="15"
                  as={Input}
                  onInputChange={() => setShowPhoneWarning(false)}
                />
              </PhoneWithPrefix>
              <Warning
                className={`edit-user__warning ${
                  showPhoneWarning ? "" : "edit-user__warning--hidden"
                }`}
                text={
                  "Podany numer telefonu jest już zarejestrowany w naszym systemie. Proszę podać inny numer."
                }
              />

              <>
                <Field
                  type="text"
                  id="email"
                  name="email"
                  label={"Email"}
                  optional
                  as={Input}
                  onInputChange={() => setShowEmailWarning(false)}
                />
                <Warning
                  className={`edit-user__warning ${
                    showEmailWarning ? "" : "edit-user__warning--hidden"
                  }`}
                  text={
                    "Podany adres email jest już zarejestrowany w naszym systemie. Proszę podać inny adres email."
                  }
                />
              </>

              {!isUserSignedRodo && (
                <>
                  <div className="edit-user__rodo-heading">Zgoda rodo</div>
                  <div className="edit-user__rodo-text">
                    Załącz plik ze zgodą lub wybierz kanał wysyłki zgody (SMS /
                    Mail).
                  </div>

                  <Warning
                    className={`${
                      showRodoCommunicationWarning
                        ? ""
                        : "edit-user__warning--hidden"
                    }`}
                    text={
                      "Musisz wybrać jedną z dostępnych form rodo: email, SMS albo plik."
                    }
                  />

                  <Field
                    label="Załącz plik"
                    name="file"
                    required
                    id="file"
                    as={DropFileInput}
                    accept=".png, .jpg"
                  />

                  <div className="edit-user__switch">
                    <div className="edit-user__switch__label">Sms</div>
                    <Switch
                      state={values.rodoPreference === "sms"}
                      onClick={() =>
                        handleChangeRodoPreference("sms", setFieldValue)
                      }
                    />
                  </div>

                  <div className="edit-user__switch">
                    <div className="edit-user__switch__label">Email</div>
                    <Switch
                      state={values.rodoPreference === "email"}
                      onClick={() =>
                        handleChangeRodoPreference("email", setFieldValue)
                      }
                    />
                  </div>
                </>
              )}

              <div className="fullscreen-modal__buttons">
                <Button
                  label="Anuluj"
                  onClick={onClose}
                  className="button--stroke button--rounded"
                />
                <Button
                  label={user ? "Zapisz" : "Dodaj"}
                  type="submit"
                  onClick={() =>
                    handleValidateRodoPreference(values.rodoPreference)
                  }
                  className=" button--rounded button--black"
                  isLoading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </FullscreenModal>
  );
};

export default EditUserByAdmin;
